import AccessKey from "./AccessKey";
import ACCESS_TRANSLATE from "./ACCESS_TRANSLATE";
import { AccessTranslate, MetadataValue } from "./interface";

interface BranchProperty {
	[AccessKey.SHOW]: boolean;
}

interface BranchKind {
	[AccessKey.METADATA]: MetadataValue;
}

type AccessCompletedBranch =
	| AccessComponent
	| BranchProperty
	| BranchKind
	| BranchContoleBtns;

interface AccessComponent {
	[key: string]: AccessCompletedBranch | undefined;
}

type DisplayFields = {
	[key in AccessKey]?: AccessCompletedBranch;
};

/**
 * Default branch properties indicating whether a component or route should be visible.
 *
 * `BRANCH_DEFAULT` allows access to display the component or route.
 * `BRANCH_NOT_SHOW` denies access to display the component or route.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const [BRANCH_DEFAULT, BRANCH_NOT_SHOW]: [BranchProperty, BranchProperty] = [
	{ [AccessKey.SHOW]: true },
	{ [AccessKey.SHOW]: false },
];

/**
 * Branch kinds representing different access levels for various types of interface elements.
 *
 * `BRANCH_DEFAULT_MENU` corresponds to the menu access level.
 * `BRANCH_DEFAULT_PAGES` corresponds to the pages access level.
 * `BRANCH_DEFAULT_TABS` corresponds to the tabs access level.
 * `BRANCH_DEFAULT_BUTTONS` corresponds to the buttons access level.
 * `BRANCH_DEFAULT_WIDGETS` corresponds to the widgets access level.
 */
const [
	BRANCH_DEFAULT_MENU,
	BRANCH_DEFAULT_PAGES,
	BRANCH_DEFAULT_TABS,
	BRANCH_DEFAULT_BUTTONS,
	BRANCH_DEFAULT_WIDGETS,
	BRANCH_DEFAULT_MODALS,
	BRANCH_DEFAULT_FIELDS,
	BRANCH_DEFAULT_CHECK_BOXS,
	BRANCH_DEFAULT_CARD_ORDERS,
	BRANCH_DEFAULT_SELECTOR,
	BRANCH_DEFAULT_EDITORS,
	BRANCH_DEFAULT_RADIO_BUTTONS,
	BRANCH_DEFAULT_PERMISSION,
]: BranchKind[] = [
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.MENU },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.PAGES },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.TABS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.BUTTONS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.WIDGETS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.MODALS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.FIELDS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.CHECK_BOXS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.CARD_ORDERS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.SELECTOR },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.EDITORS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.RADIO_BUTTONS },
	{ ...BRANCH_DEFAULT, [AccessKey.METADATA]: AccessKey.PERMISSION },
];

type ControlButtonKeys =
	| AccessKey.CONTROL_BUTTON_ADD
	| AccessKey.CONTROL_BUTTON_EDIT
	| AccessKey.CONTROL_BUTTON_DELETE;

type BranchContoleBtns = Record<ControlButtonKeys, BranchProperty>;

const BRANCH_CONTROL_BUTTONS = {
	[AccessKey.CONTROL_BUTTON_ADD]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.CONTROL_BUTTON_DELETE]: BRANCH_DEFAULT_BUTTONS,
};

const BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS = {
	...BRANCH_CONTROL_BUTTONS,
	[AccessKey.AFTER_ADDITIONAL_BUTTONS]: BRANCH_DEFAULT_BUTTONS,
};

const BRANCH_PANEL_POINT = {
	[AccessKey.ADD_POINT]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.INSERT_POINT]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.EDIT_POINT]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.DELETE_POINT]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.MOVE_POINT_UP]: BRANCH_DEFAULT_BUTTONS,
	[AccessKey.MOVE_POINT_DOWN]: BRANCH_DEFAULT_BUTTONS,
};

const ACCESS_TO_THE_COMPONENT: DisplayFields = {
	[AccessKey.LOGIN]: BRANCH_DEFAULT_PAGES,
	[AccessKey.MAIN]: BRANCH_DEFAULT_PAGES,
	//* Menu: level 1
	[AccessKey.ORDERS]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs

		[AccessKey.LIVE_ORDERS]: BRANCH_DEFAULT_TABS,
		[AccessKey.PRELIMINARY_ORDERS]: BRANCH_DEFAULT_TABS,
		[AccessKey.EXECUTING_ORDERS]: BRANCH_DEFAULT_TABS,
		[AccessKey.ALL_ORDERS]: BRANCH_DEFAULT_TABS,
		[AccessKey.CLOSED_ORDERS]: BRANCH_DEFAULT_TABS,

		//* Btns 2 row
		[AccessKey.NEW_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.EDIT_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.REVERT_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.CLOSED_ORDER]: BRANCH_DEFAULT_BUTTONS,
		//
		[AccessKey.COPY_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SET_ORDER_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.APPROVE_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SEND_ORDER_AGAIN]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.RECALCULATE_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.RECALCULATE_ORDER_WITH_ACTUAL_RATES]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.RETURN_ORDER_ON_AIR_OR_REMOVE_EXECUTOR]:
			BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SEND_ORDER_TO_AIR]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SEND_ORDER_TO_EXECUTING]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.TOGGLE_ORDER_AIR]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SET_ORDER_CALL_TO_CLIENT_BEING_REQUIRED]:
			BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SET_ORDER_CALL_TO_CLIENT_SUCCESS]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SET_ORDER_CALL_TO_EXECUTOR_BEING_REQUIRED]:
			BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SET_ORDER_CALL_TO_EXECUTOR_SUCCESS]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.COLOR_ORDER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.CALL_ORDER_CLIENT]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.CALL_ORDER_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SMS_BUTTON]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.AUTO_CALL_CLIENT_BUTTON]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.ORDER_RETURN_FROM_TRANSFER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.ORDER_SEND_TO_TRANSFER]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.ORDER_SEND_TO_TRANSFER_LOCAL]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.ORDER_SEND_TO_TRANSFER_GLOBAL]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.ORDER_SEND_TO_TRANSFER_OUTSIDE]: BRANCH_DEFAULT_BUTTONS,

		//* Btns 1 row
		[AccessKey.SHOW_ONLY_MY_ORDERS]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SHOW_ORDERS_WITHOUT_EXECUTORS]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SEARCH_CAR]: {
			...BRANCH_DEFAULT_BUTTONS,
			//* radioButtons
			[AccessKey.FLEET_NUMBER]: BRANCH_DEFAULT_RADIO_BUTTONS,
			[AccessKey.LINCENSE_PLATE]: BRANCH_DEFAULT_RADIO_BUTTONS,
		},
		[AccessKey.SEARCH_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
		[AccessKey.SEARCH_ORDER]: {
			...BRANCH_DEFAULT_BUTTONS,
			//* fields
			[AccessKey.PHONE]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.FROM]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.TO]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.FILTER_ORDERS_BY_MULTY]: {
			...BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.CAR_CLASSES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.SERVICES]: BRANCH_DEFAULT_SELECTOR,
		},

		//* Widget
		[AccessKey.WIDGET_ORDER_PARKING]: {
			...BRANCH_DEFAULT_WIDGETS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selectors
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.EXECUTOR_TEAMS]: BRANCH_DEFAULT_SELECTOR,
				//* Fields
				[AccessKey.PARKING_GROUP_LIST]: BRANCH_DEFAULT_FIELDS,
				//* Checkboxs
				[AccessKey.STATUS]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.IS_WORKING]: BRANCH_DEFAULT_CHECK_BOXS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.LOWERS_STATISTICS_TABLE]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.WIDGET_ORDER_EXECUTORS]: {
			...BRANCH_DEFAULT_WIDGETS,
			//* Btns
			[AccessKey.CHANGE_PERFORMERS_STATUS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CHANGE_PERFORMERS_STATUS_SELECTED]:
				BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CALL_ORDER_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selectors
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.CAR_PARKS]: BRANCH_DEFAULT_SELECTOR,
				[AccessKey.EXECUTOR_TEAMS]: BRANCH_DEFAULT_SELECTOR,
				//* Checkboxs
				[AccessKey.STATUS]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.IS_WORKING]: BRANCH_DEFAULT_CHECK_BOXS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.LOWERS_STATISTICS_TABLE]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.WIDGET_ORDER_MAP]: {
			...BRANCH_DEFAULT_WIDGETS,
			//* Btns
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selectors
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.CAR_PARKS]: BRANCH_DEFAULT_SELECTOR,
				[AccessKey.EXECUTOR_TEAMS]: BRANCH_DEFAULT_SELECTOR,
				//* Checkboxs
				[AccessKey.BY_ORDER]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.STATUS]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.IS_WORKING]: BRANCH_DEFAULT_CHECK_BOXS,
			},
			[AccessKey.MAP_FILTER_POINTS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAP_FILTER_PRICE_ZONE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAP_FILTER_SECTOR]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAP_FILTER_PARKING]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAP_FILTER_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAP_FILTER_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.WIDGET_ORDER_ORDER_DETAILS]: {
			...BRANCH_DEFAULT_WIDGETS,
			//* Btns
			...BRANCH_PANEL_POINT,
			[AccessKey.OUT_OF_TOWN_DISTANCE]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.IDLE_TIME]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.PRICE]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.ADDITIONAL_PRICE]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.AWAITING_TIME]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.HOURLY_TIME]: BRANCH_DEFAULT_EDITORS,
		},
		[AccessKey.WIDGET_ORDER_CHAT]: {
			...BRANCH_DEFAULT_WIDGETS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selectors
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.EXECUTORS]: BRANCH_DEFAULT_SELECTOR,
				[AccessKey.CAR_PARKS]: BRANCH_DEFAULT_SELECTOR,
				[AccessKey.EXECUTOR_TEAMS]: BRANCH_DEFAULT_SELECTOR,
			},
			[AccessKey.RELEVANCE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CHOISE_CUSTOMERS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SEND_SMS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.REPLY_TO_A_MESSAGE]: BRANCH_DEFAULT_BUTTONS,
		},

		//* Mods
		[AccessKey.ORDER_MODAL]: {
			...BRANCH_DEFAULT_CARD_ORDERS,
			//* Btns
			...BRANCH_PANEL_POINT,
			[AccessKey.CLOSED_ORDER]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.COPY_ORDER]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SMS_BUTTON]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CALL_ORDER_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CALL_ORDER_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_CLIENT_NUMBER_TO_EXECUTOR]: BRANCH_DEFAULT_BUTTONS,
			//* Btns filter
			[AccessKey.STREET]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.LOCAL_OBJECT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.OBJECT]: BRANCH_DEFAULT_BUTTONS,
			//* Btns main
			[AccessKey.CLOSE_ALL]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CANCEL]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SAVE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.ADD_NEW_ORDER]: BRANCH_DEFAULT_BUTTONS,
			//* Btns statistic
			[AccessKey.OUT_OF_TOWN_DISTANCE]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.IDLE_TIME]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.PRICE]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.ADDITIONAL_PRICE]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.AWAITING_TIME]: BRANCH_DEFAULT_EDITORS,
			[AccessKey.HOURLY_TIME]: BRANCH_DEFAULT_EDITORS,
			//* Btns map
			[AccessKey.ROUTE_MAP]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.PRICE_ZONES_MAP]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SECTORS_MAP]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.PARKINGS_MAP]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.DRIVER_MAP]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CLIENT_MAP]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CENTER_MAP]: BRANCH_DEFAULT_BUTTONS,
			//* Tabs
			[AccessKey.MAP]: BRANCH_DEFAULT_TABS,
			[AccessKey.EVENTS]: BRANCH_DEFAULT_TABS,
			[AccessKey.CONVERSATIONS]: BRANCH_DEFAULT_TABS,
			//* checkBoxs OrderRideInformation
			[AccessKey.AUTO_SUPPLY]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.OUT_OF_TOWN]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.SUPPLY]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.HOURLY]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.TAXIMETER]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.DISCOUNT_PLAN]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.BONUS_TRIP]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.BONUS_BALANCE]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.ACCUMULATIVE_DISCOUNT]: BRANCH_DEFAULT_CHECK_BOXS,
			[AccessKey.MAX_BONUSES_AND_INPUT]: BRANCH_DEFAULT_CHECK_BOXS,
			//* selects OrderInformation
			[AccessKey.ORDER_TIME]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.PAYMENT_TYPE]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.TARIFF]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.PASSENGER_COUNT]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.CAR_CLASSES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.SERVICES]: BRANCH_DEFAULT_SELECTOR,
			//* selects head
			[AccessKey.TRANSPORTATION_TYPE]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			//* field
			[AccessKey.PERSONAL_INFORMATION]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.POINT_TABLE]: BRANCH_DEFAULT_FIELDS,
			//* permissions
			[AccessKey.VIEW]: BRANCH_DEFAULT_PERMISSION,
			[AccessKey.EDIT]: BRANCH_DEFAULT_PERMISSION,
		},
	},
	[AccessKey.CUSTOMERS]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs
		[AccessKey.CLIENTS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.SHOW_ACTIVE_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_NO_ACTIVE_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CALL]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SENDING_SMS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SENDEING_PUSH]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BALANSE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BONUS_BALANSE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selectors
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.ORDER_COUNT]: BRANCH_DEFAULT_SELECTOR,
				//* Checkboxs
				[AccessKey.ONLY_MOBILE_APP]: BRANCH_DEFAULT_CHECK_BOXS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.COUNTERPARTIES]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.SHOW_ACTIVE_COUNTERPARTIES]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_NO_ACTIVE_COUNTERPARTIES]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.AGENTS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.CALL]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SENDING_SMS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SENDEING_PUSH]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BALANSE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BONUS_BALANSE]: BRANCH_DEFAULT_BUTTONS,
		},
		[AccessKey.CLIENT_GROUPS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
		},
		[AccessKey.COUNTERPARTY_GROUPS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
		},
		[AccessKey.AGENT_GROUPS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
		},
	},
	[AccessKey.BLACKLIST]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs
		[AccessKey.BLACKLIST_CUSTOMERS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.BLACKLIST_EXECUTORS]: BRANCH_DEFAULT_TABS,
		[AccessKey.BLACKLIST_TRANSPORT]: BRANCH_DEFAULT_TABS,
	},
	[AccessKey.PREFERENCES_PAGE_NAME]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs
		[AccessKey.CAR_MODELS_AND_BRANDS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			[AccessKey.CONTROL_BUTTON_ADD]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CONTROL_BUTTON_DELETE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selectors
				[AccessKey.FAVORITES_AND_NON_FAVORITES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.ACTIVE_AND_INACTIVE]: BRANCH_DEFAULT_SELECTOR,
				[AccessKey.BASE_AND_CREATED]: BRANCH_DEFAULT_SELECTOR,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
			//* Selectors
			[AccessKey.CAR_OPTIONS]: BRANCH_DEFAULT_SELECTOR,
		},
		[AccessKey.CAR_CLASSES]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.TABLE_NAVIGATION]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.COLORS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,

			[AccessKey.FILTER]: BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.OBJECTS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Check Box
				[AccessKey.SHOW_ALL]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.SHOW_OBJECTS]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.SHOW_STREETS]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.HIDDEN_IN_CLIENT_APP]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.HIDE_INACTIVE]: BRANCH_DEFAULT_CHECK_BOXS,
			},
			[AccessKey.IMPORT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.EXPORT]: BRANCH_DEFAULT_BUTTONS,

			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Radio_btns
			[AccessKey.OBJECTS_AND_OBJECT_GROUPS]: BRANCH_DEFAULT_RADIO_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.SECTORS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.PARKINGS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.PRICE_ZONES]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.SERVICES]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,

			[AccessKey.FILTER]: BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.LANGUAGE]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
	},
	[AccessKey.ACCOUNTS]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs
		[AccessKey.EXECUTORS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,

			[AccessKey.SHOW_ACTIVE_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_NO_ACTIVE_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CALL]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SENDING_SMS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SENDEING_PUSH]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BALANSE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BONUS_BALANSE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.TRANSPORT]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,

			[AccessKey.SHOW_ACTIVE_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_NO_ACTIVE_CLIENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.USERS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			[AccessKey.STATUS]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.ROLES]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.CAR_PARKS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.SCHEDULES]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.EXECUTOR_TEAMS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			...BRANCH_CONTROL_BUTTONS,
		},
	},
	[AccessKey.REPORTS]: {
		...BRANCH_DEFAULT_MENU,
		//* Menu: level 2
		[AccessKey.REPORTS_ACCOUNTING]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.CASHLESS_ORDERS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Selecters
					[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
						BRANCH_DEFAULT_SELECTOR,
					[AccessKey.CONTRACTOR]: BRANCH_DEFAULT_SELECTOR,
					[AccessKey.ACCOUNTS]: BRANCH_DEFAULT_SELECTOR,
					[AccessKey.CARDS]: BRANCH_DEFAULT_SELECTOR,
					//* Check boxs
					[AccessKey.ONLY_SUCCESSFUL]: BRANCH_DEFAULT_CHECK_BOXS,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
			[AccessKey.CASHLESS_ORDERS_GENERAL]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Selecters
					[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
						BRANCH_DEFAULT_SELECTOR,
					//* Check boxs
					[AccessKey.ONLY_SUCCESSFUL]: BRANCH_DEFAULT_CHECK_BOXS,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.GROUP_ACTION]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
			[AccessKey.CASHLESS_ORDERS_PERIOD_REPORT]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Selecters
					[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
						BRANCH_DEFAULT_SELECTOR,
					[AccessKey.CONTRACTOR]: BRANCH_DEFAULT_SELECTOR,
					//* Check boxs
					[AccessKey.ADD_ONLY_IF_OPERATIONS_IN_PERIOD]:
						BRANCH_DEFAULT_CHECK_BOXS,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
			[AccessKey.CASHLESS_ACCOUNTS_SUMMARY_REPORT]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Selecters
					[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
						BRANCH_DEFAULT_SELECTOR,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
			[AccessKey.FUEL_DEDUCTION_ORDERS_REPORT]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
			[AccessKey.AGENT_ORDERS_REPORT]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Selecters
					[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
						BRANCH_DEFAULT_SELECTOR,
					//* Check boxs
					[AccessKey.ONLY_SUCCESSFUL]: BRANCH_DEFAULT_CHECK_BOXS,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
			[AccessKey.ACCOUNTS_REPORT]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Selecters
					[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
						BRANCH_DEFAULT_SELECTOR,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
		},
		[AccessKey.REPORTS_CHARTS]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.GENERAL]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.GENERATE]: BRANCH_DEFAULT_BUTTONS,
				//* Selecters
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				[AccessKey.CHART_TYPE]: BRANCH_DEFAULT_SELECTOR,
				[AccessKey.TIME_TYPE]: BRANCH_DEFAULT_SELECTOR,
			},
		},
		[AccessKey.REPORTS_SALARY]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.DISPATCHERS_ACRIVITY]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.FORM_REPORT_AND_FILTER]: {
					...BRANCH_DEFAULT_BUTTONS,
					//* Btns
					[AccessKey.DATE]: BRANCH_DEFAULT_BUTTONS,
				},
				[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
				[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			},
		},
	},
	[AccessKey.ARCHIVES]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs
		[AccessKey.ORDERS]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			//* Btns
			[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.REVERT_ORDER_TO_ARCHIVE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.EXPORT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selecters
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				//* CheckBox
				[AccessKey.ARCHIVE_FILTER_EXECUTOR]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_STATUS_ORDER]:
					BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_NUMBER_ORDER]:
					BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_NUMBER_PHONE]:
					BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_STREET]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_CLIENT]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_COUNTERPARTY]:
					BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_AGENT]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_PAYMENTS]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_CLASSES]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_SERVICES]: BRANCH_DEFAULT_CHECK_BOXS,
				[AccessKey.ARCHIVE_FILTER_SECTORS]: BRANCH_DEFAULT_CHECK_BOXS,
			},
		},
		[AccessKey.BANK_TRANSACTIONS]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			//* Btns
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Fields
				[AccessKey.TYPES]: BRANCH_DEFAULT_FIELDS,
				[AccessKey.STATUSES]: BRANCH_DEFAULT_FIELDS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.TRANSACTIONS]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			//* Btns
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selecters
				[AccessKey.FROM_OR_TO]: BRANCH_DEFAULT_SELECTOR,
				//* Fields
				[AccessKey.TRANSACTION_NUMBER_INPUT]: BRANCH_DEFAULT_FIELDS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.GPS_LOG]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			[AccessKey.COMPANIES_AND_TAXI_SERVICES]: BRANCH_DEFAULT_SELECTOR,
			//* Fields
			[AccessKey.CALLSIGN]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.LINCENSE_PLATE]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.PARK_NUMBER]: BRANCH_DEFAULT_FIELDS,
			//* Btns
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
		},
		[AccessKey.MESSAGES]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			//* Btns
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selecters
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				//* Fields
				[AccessKey.TYPES]: BRANCH_DEFAULT_FIELDS,
				[AccessKey.STATUSES]: BRANCH_DEFAULT_FIELDS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
		[AccessKey.PUSH]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			//* Btns
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selecters
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
				//* Fields
				[AccessKey.TYPES]: BRANCH_DEFAULT_FIELDS,
				[AccessKey.STATUSES]: BRANCH_DEFAULT_FIELDS,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},

		[AccessKey.CALL_HISTORY]: {
			...BRANCH_DEFAULT_TABS,
			//* Selecters
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
			//* Btns
			[AccessKey.FILTER]: {
				...BRANCH_DEFAULT_BUTTONS,
				//* Selecters
				[AccessKey.COMPANIES_AND_TAXI_SERVICES]:
					BRANCH_DEFAULT_SELECTOR,
			},
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
		},
	},
	[AccessKey.ACCOUNTING]: {
		...BRANCH_DEFAULT_PAGES,
		//* Tabs
		[AccessKey.EXECUTORS_GENERAL]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			[AccessKey.REFRESH]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.BALANCE_CHANGE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SAVE_AS_FILE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.PRINT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.FILTER]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.SEARCH]: BRANCH_DEFAULT_FIELDS,
			//* Selectors
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
		},
		[AccessKey.EXECUTORS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns

			[AccessKey.BALANCE_CHANGE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CALCULATE_FOR_DAY]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.ASSIGN_FINE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.DRIVER_WORK_HISTORY]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_ACTIVE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_INACTIVE]: BRANCH_DEFAULT_BUTTONS,
			//
			[AccessKey.BALANCE_CHANGE_LOWER]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.VIEW_ENTRY_LOWER]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE_LOWER]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.DRIVER_SUMMARY_LOWER]: BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.DATE_LOWER]: BRANCH_DEFAULT_SELECTOR,
		},
		[AccessKey.BANK_ACCOUNTS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CONTROL_BUTTON_DELETE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_ACTIVE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_INACTIVE]: BRANCH_DEFAULT_BUTTONS,
			//
			[AccessKey.EDIT_CARD]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.DELETE_CARD]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE_CARD]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_ACTIVE_CARDS]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_INACTIVE_CARDS]: BRANCH_DEFAULT_BUTTONS,
			//
			[AccessKey.BALANCE_CHANGE_SETTLEMENT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE_SETTLEMENT]: BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.DATE_SETTLEMENT]: BRANCH_DEFAULT_SELECTOR,
		},
		[AccessKey.AGENTS]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.CONTROL_BUTTON_DELETE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_ACTIVE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.SHOW_INACTIVE]: BRANCH_DEFAULT_BUTTONS,
			//
			[AccessKey.BALANCE_CHANGE_LOWER]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.UPDATE_LOWER]: BRANCH_DEFAULT_BUTTONS,
			//* Selectors
			[AccessKey.DATE_LOWER]: BRANCH_DEFAULT_SELECTOR,
		},
		[AccessKey.REPLENISH_PERFORMERS_BALANCE]: {
			...BRANCH_DEFAULT_TABS,
			//* Btns
			[AccessKey.UPDATE]: BRANCH_DEFAULT_BUTTONS,
			[AccessKey.MAGNIFYING_GLASS]: BRANCH_DEFAULT_BUTTONS,
			//* Fields
			[AccessKey.PAYMENT_TYPE_NAME]: BRANCH_DEFAULT_FIELDS,
			[AccessKey.PAYMENT_METHOD]: BRANCH_DEFAULT_FIELDS,
			//* Selectors
			[AccessKey.DATE]: BRANCH_DEFAULT_SELECTOR,
		},
	},
	[AccessKey.SETTINGS]: {
		...BRANCH_DEFAULT_MENU,
		//* Menu: level 2
		[AccessKey.SETTINGS_ORDERS]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.ORDERS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
			[AccessKey.ORDERS_DISTRIBUTION]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
			[AccessKey.FEED_TIME_CALCULATION]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
		},
		[AccessKey.SETTINGS_TARIFFS]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.MAIN_TARIFFS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
			[AccessKey.AUTOMATIC_TARIFFS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
			[AccessKey.GENERAL_TARIFFS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
			[AccessKey.ADDITIONAL]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				[AccessKey.CONTROL_BUTTON_EDIT]: BRANCH_DEFAULT_BUTTONS,
			},
		},
		[AccessKey.SETTINGS_FINANCES]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.EXECUTOR_TARIFF_PLANS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
			[AccessKey.CUSTOMER_TARIFF_PLANS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
			[AccessKey.USER_TARIFF_PLANS]: BRANCH_DEFAULT_TABS,
			[AccessKey.EXECUTOR_PENALTIES]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
			[AccessKey.CUSTOMER_PENALTIES]: BRANCH_DEFAULT_TABS,
			[AccessKey.USER_PENALTIES]: BRANCH_DEFAULT_TABS,
		},
		[AccessKey.SETTINGS_MESSAGES]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.PROVIDER_SMS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
			[AccessKey.TEMPLATES_SMS]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
			[AccessKey.PUSH_NOTICES]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
		},
		[AccessKey.SETTINGS_MOBILE_APPS]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.MOBILE_APP_EXECUTOR]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
			[AccessKey.MOBILE_APP_CLIENT]: BRANCH_DEFAULT_TABS,
		},
		[AccessKey.SETTINGS_PARAMETERS]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.APPLICATION]: BRANCH_DEFAULT_TABS,
			[AccessKey.EXECUTOR]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_BUTTONS,
			},
		},
		[AccessKey.SETTINGS_MAP]: BRANCH_DEFAULT_PAGES,
		[AccessKey.SETTINGS_ORDER_EXCHANGE]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.MAIN]: BRANCH_DEFAULT_TABS,
		},
		[AccessKey.SETTINGS_PAYMENT_SYSTEMS]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.CUSTOMER]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
			[AccessKey.EXECUTOR]: {
				...BRANCH_DEFAULT_TABS,
				//* Btns
				...BRANCH_CONTROL_AND_ADDITIONAL_BUTTONS,
			},
		},
		[AccessKey.SETTINGS_INTERFACE]: {
			...BRANCH_DEFAULT_PAGES,
			//* Tabs
			[AccessKey.COLORS]: BRANCH_DEFAULT_TABS,
			[AccessKey.FONT]: BRANCH_DEFAULT_TABS,
			[AccessKey.KEYBINDS]: BRANCH_DEFAULT_TABS,
		},
		[AccessKey.SETTINGS_VOIP]: BRANCH_DEFAULT_PAGES,
	},
};

export { AccessKey, ACCESS_TRANSLATE, ACCESS_TO_THE_COMPONENT };
export type {
	AccessTranslate,
	AccessComponent,
	DisplayFields,
	AccessCompletedBranch,
};
